import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import clsx from "clsx";

interface MultiSelectOption {
  id: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  selected: string[];
  onToggle: (id: string, selected: boolean) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
}

export default function MultiSelectDropdown({
  options,
  selected,
  onToggle,
  placeholder = "None",
  disabled,
  readOnly,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const labelFor = (id: string) =>
    options.find((o) => o.id === id)?.label ?? id;

  const chips =
    selected.length === 0 ? (
      <span className="text-sm text-gray-400 dark:text-gray-500">
        {placeholder}
      </span>
    ) : (
      selected.map((id) => (
        <span
          key={id}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
        >
          {labelFor(id)}
        </span>
      ))
    );

  if (readOnly) {
    return <div className="flex flex-wrap gap-1">{chips}</div>;
  }

  return (
    <div ref={containerRef} className="relative inline-block min-w-[12rem]">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="input flex w-full items-center justify-between gap-2 py-1 text-left disabled:opacity-50"
      >
        <span className="flex flex-wrap gap-1">{chips}</span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
      </button>
      {open && (
        <ul className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {options.map((option) => {
            const isSelected = selected.includes(option.id);
            return (
              <li key={option.id}>
                <button
                  type="button"
                  disabled={disabled}
                  aria-pressed={isSelected}
                  onClick={() => onToggle(option.id, !isSelected)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <span
                    className={clsx(
                      "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border",
                      isSelected
                        ? "border-primary-600 bg-primary-600"
                        : "border-gray-300 bg-white dark:border-gray-500 dark:bg-gray-700",
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </span>
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
